/**
 * `ref` del asistente de ventas de la web (Agentes): el id PÚBLICO de la
 * conversación (`sc_` + alfanumérico) que el bot pega al deep-link del checkout
 * (`?ref=sc_…&utm_source=ventas-web`). Es lo que permite atribuir la compra a la
 * conversación que la trajo.
 *
 * El patrón está DUPLICADO a propósito de `@repo/sales-chat`
 * (`SALES_CHAT_REF_PATTERN` en `domain/checkout-url.ts`): el checkout no depende
 * de ese paquete y añadirlo arrastraría su árbol al bundle. Si cambia allí,
 * cambia aquí. El API vuelve a validarlo, así que aquí solo evita llamadas inútiles.
 *
 * NO es dato personal: es un identificador opaco de conversación.
 */
export const SALES_CHAT_REF_PATTERN = /^sc_[A-Za-z0-9]{8,64}$/

export const isSalesChatRef = (value: unknown): value is string =>
  typeof value === 'string' && SALES_CHAT_REF_PATTERN.test(value)

/**
 * El `ref` vive en sessionStorage, NO en el store de tracking (localStorage):
 * localStorage sobrevive al cierre del navegador y una visita meses después
 * (sin bot) reenviaría el `ref` viejo en cada paso y atribuiría al bot una compra
 * que no trajo. sessionStorage muere con la pestaña: el `ref` solo acompaña a la
 * sesión de compra en la que el lead aterrizó desde el chat. Sobrevive a los
 * pasos del funnel (misma pestaña) aunque la URL se limpie.
 */
const SESSION_KEY = 'sales-chat-ref'

/** Guarda el `ref` de esta sesión de compra (solo con formato válido). Nunca lanza. */
export const rememberSalesChatRef = (ref: unknown): void => {
  if (!isSalesChatRef(ref)) return
  try {
    window.sessionStorage.setItem(SESSION_KEY, ref)
  } catch {
    // Modo privado / storage bloqueado: se pierde la atribución, no el checkout.
  }
}

/** `ref` de esta sesión de compra, o null si no aterrizó desde el chat. Nunca lanza. */
export const recallSalesChatRef = (): string | null => {
  try {
    const ref = window.sessionStorage.getItem(SESSION_KEY)
    return isSalesChatRef(ref) ? ref : null
  } catch {
    return null
  }
}

/**
 * Quita el `ref` del bot de los query params que van al store de tracking
 * (localStorage). Cualquier otro `ref` (afiliados, campañas) se deja como estaba.
 */
export const withoutSalesChatRef = <T extends Record<string, unknown>>(
  params: T
): T => {
  if (!isSalesChatRef(params?.ref)) return params
  const { ref: _ref, ...rest } = params
  return rest as T
}
